import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Combobox } from '@node-space/ui-components'
import { useRewardsWalletsOptions } from 'pages/Rewards/hooks/useRewardsWalletsOptions'

interface RewardsWalletSelectProps {
  name: string
  selectedCurrency: string
  disabled?: boolean
  testId?: string
  label?: string
  setMaxAmount?: (value: string) => void
  setWalletName?: (value: string) => void
}

export const RewardsWalletSelect = ({
  name,
  selectedCurrency,
  disabled = false,
  testId = 'wallet-select-combobox',
  label,
  setWalletName,
  setMaxAmount,
}: RewardsWalletSelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  const {
    rewardsWallets,
    rewardsWalletsOptions,
    isLoading: isLoadingRewardsWallets,
    onSearchChange: setSearchRewardsWallets,
  } = useRewardsWalletsOptions({
    selectedCurrency,
    selectedWalletId: '',
  })

  const handleSetWalletName = (walletName: string) => {
    if (name !== 'allocateFrom' && setWalletName) {
      setWalletName(walletName)
    }
  }

  const handleSetMaxAmount = (walletId: string) => {
    if (setMaxAmount) {
      setMaxAmount(
        rewardsWallets?.find(wallet => wallet.id === walletId)?.balance?.value || '0'
      )
    }
  }

  const handleOnChange = (value: string) => {
    const selectedWallet = rewardsWalletsOptions.find(wallet => wallet.value === value) ?? null
    const walletName = selectedWallet?.leftText?.toString() || ''

    field.onChange({
      value: selectedWallet?.value,
      name: walletName,
    })

    handleSetMaxAmount(selectedWallet?.value || '')
    handleSetWalletName(walletName)
  }

  return (
    <Combobox
      {...field}
      onChange={handleOnChange}
      key={`${name}-${selectedCurrency}`}
      id={`wallet-select-${name}`}
      label={label || t('rewards.deposit.depositFrom')}
      loading={isLoadingRewardsWallets}
      selectPlaceholder={t('select')}
      items={rewardsWalletsOptions}
      errorText={error?.message}
      testId={testId}
      onInputChange={setSearchRewardsWallets}
      disabled={disabled || !selectedCurrency}
      defaultValue={field.value}
    />
  )
}
